import { Project } from '../types/project';
import { CalculationResults } from './calculations';
import { calcRulesFor } from './calc/rulesVersion';
import { getEditionPack, resolvedEditionKey } from './calc/editions';

// The report paragraph that names TrueLoad's reading of 120.87 (2026)
// Condition (2) — new load at the Table 120.83 percentages — and prints the
// full-rating result beside it, so an inspector who reads the sentence the
// other way has the number in front of them without a second report.
//
// One builder for both report renderers (utils/reportGenerator.ts HTML and
// components/ReportView.tsx), so the two cannot word the position
// differently. Null wherever the 2026 position is not in play.

const fmt = (n: number) => n.toFixed(2);

export function newLoadFactorDisclosure(
  project: Partial<Project>,
  results: CalculationResults,
  cite: string
): string | null {
  const pack = getEditionPack(resolvedEditionKey(project));
  const rates = pack.new83Percentages;
  if (!rates || calcRulesFor(project) < 2) return null;

  if (!results.newLoadFactorsApplied) {
    return (
      `Under the ${pack.edition} edition, NEC ${cite} Condition (2) determines the new load in accordance with ` +
      `Article 120 Parts I, III, and IV, which TrueLoad reads as permitting the demand factors of Table 120.83. ` +
      `The preparer elected not to apply them: every concurrent new load is entered at its full rating.`
    );
  }

  const pct = (f: number) => `${Math.round(f * 100)} percent`;
  const tier =
    results.newLoadTierFillApplied
      ? ` Table 120.83 applies its first ${pack.optional83TierKVA} kVA at 100 percent to existing and new load combined; ` +
        `the adjusted existing demand of ${fmt(results.existingDemand125KVA)} kVA leaves part of that tier unused, and this ` +
        `evaluation fills it from new load at 100 percent, lowest percentage first, the order that gives the highest demand.`
      : '';

  return (
    `Under the ${pack.edition} edition, NEC ${cite} Condition (2) adds the new load, less any removed load, determined in ` +
    `accordance with Article 120 Parts I, III, and IV. This evaluation reads that phrase as governing the new load and ` +
    `applies the demand factors of Table 120.83, the Part IV method for loads added to an existing dwelling: new EVSE at ` +
    `${pct(rates.newEvse)}, new central electric resistance space heating at ${pct(rates.newCentralResistanceHeat)}, ` +
    `all other new loads at ` +
    `${pct(rates.otherNewLoads)}. Equipment not expected to operate in the season of the measured peak is first excluded ` +
    `as noncoincident under ${pack.sectionMap.noncoincident ?? 'Part I'}; the factors apply to the loads that remain.` +
    (calcRulesFor(project) >= 4
      ? ' A heat pump whose rating includes backup strip heat is counted as central electric resistance space heating, ' +
        'because the strips cannot be separated from the rating entered.'
      : '') +
    tier +
    (results.totalRemovedCreditKVA > 0
      ? ' Removed load is credited separately, as described below.'
      : ' No removed load is credited.') +
    ` The sentence can also be read as governing only the removed load, which would leave ` +
    `new load at its full rating. That is TrueLoad's reading of an ambiguous provision, not a determination the Code ` +
    `makes, and the authority having jurisdiction may require the full rating. At full rating the concurrent new load ` +
    `is ${fmt(results.totalConcurrentNewLoadKVA)} kVA, the adjusted demand ${fmt(results.adjustedDemandAt100KVA)} kVA, ` +
    `and the result ${results.adequacyAt100}.`
  );
}
